import { queryClient } from '@gen2/config';
import { useSafeQuery } from '@gen2/hooks';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { has } from 'lodash';
import {
  addContactInRequest,
  createInviteRequest,
  deleteContactInRequest,
  deleteInviteRequests,
  getInviteRequests,
  TInviteRequestQueryParams,
  TInviteRequests,
  updateInviteRequest,
  inviteRequestReorder,
  templateRequestReorder,
  followUpRequestReorder,
  TInviteRequestReorderPayload,
  getInviteRequestGroups,
  TInviteRequestGroup,
  updateRequestGroupName,
  duplicateInviteGroupRequest,
} from './api';

export const InviteRequestsKeys = {
  getInviteRequests: 'getInviteRequests',
  updateInviteRequest: 'updateInviteRequest',
  getInviteRequestGroups: 'getInviteRequestGroups',
};

export const useCreateInviteRequestMutation = () =>
  useMutation({ mutationFn: createInviteRequest });

export const useInviteRequestsQuery = (
  params: TInviteRequestQueryParams,
  options?: UseQueryOptions<TInviteRequests['data']['requests']>,
) => {
  return useSafeQuery<TInviteRequests['data']['requests']>(
    [InviteRequestsKeys.getInviteRequests, Object.values(params).toString()],
    async () => {
      const res = await getInviteRequests(params);

      return res?.data.data.requests;
    },
    options,
  );
};

export const useDeleteInviteRequestMutation = () =>
  useMutation({ mutationFn: deleteInviteRequests });

export const useUpdateInviteRequestMutation = () =>
  useMutation({
    mutationKey: [InviteRequestsKeys.updateInviteRequest],
    mutationFn: updateInviteRequest,
  });

export const useAddContactInRequestMutation = () =>
  useMutation({
    mutationFn: addContactInRequest,
  });

export const useDeleteContactInRequestMutation = () =>
  useMutation({
    mutationFn: deleteContactInRequest,
  });

export const useInviteRequestReorder = () => {
  return useMutation({
    mutationFn: (payload: TInviteRequestReorderPayload) =>
      inviteRequestReorder(payload),
  });
};

export const useTemplateRequestReorder = () => {
  return useMutation({
    mutationFn: (payload: TInviteRequestReorderPayload) =>
      templateRequestReorder(payload),
  });
};

export const useFollowUpRequestReorder = () => {
  return useMutation({
    mutationFn: (payload: TInviteRequestReorderPayload) =>
      followUpRequestReorder(payload),
  });
};

//getInviteRequestGroups
export const useGetInviteRequestGroupsQuery = (
  inviteId: string,
  options?: UseQueryOptions<Array<TInviteRequestGroup>>,
) => {
  return useQuery<Array<TInviteRequestGroup>>({
    queryKey: [InviteRequestsKeys.getInviteRequestGroups, inviteId],
    queryFn: async () => {
      const { data } = await getInviteRequestGroups({ inviteId });

      if (data && has(data, 'data') && has(data.data, 'request_groups')) {
        return data.data.request_groups;
      }

      return [];
    },
    ...options,
  });
};

//updateInviteRequestGroupName
export const useUpdateInviteRequestGroupNameMutation = () => {
  return useMutation({
    mutationFn: updateRequestGroupName,
    onSuccess: async () => {
      await queryClient.invalidateQueries([
        InviteRequestsKeys.getInviteRequestGroups,
      ]);
    },
  });
};

//duplicateInviteGroupRequest
export const useDuplicateInviteGroupRequestMutation = () => {
  return useMutation({
    mutationFn: duplicateInviteGroupRequest,
    onSuccess: async () => {
      await queryClient.invalidateQueries([
        InviteRequestsKeys.getInviteRequestGroups,
      ]);
    },
  });
};
